import { hash2 } from './noise';

/**
 * Procedural "elder greeting cards" (長輩圖): bright gradient skies, a sun,
 * flowers or scenery, sparkles, and big outlined greeting text — the style
 * grandparents love to send in family chats. Returns a PNG data URL.
 */
const cache = new Map<string, string>();

const THEMES = [
  { sky: ['#ffd9a0', '#ff9fb8'], kind: 'lotus' },
  { sky: ['#9fe0ff', '#fff3c4'], kind: 'sunflower' },
  { sky: ['#ffe8f2', '#ffc6e0'], kind: 'roses' },
  { sky: ['#bfe9ff', '#e8fff0'], kind: 'mountains' },
  { sky: ['#2a2a6e', '#7a5ac8'], kind: 'moon' },
  { sky: ['#fff1c8', '#ffd28a'], kind: 'tea' },
  { sky: ['#d6f5d0', '#a8e6ff'], kind: 'bamboo' },
  { sky: ['#ffd0a8', '#ff8a8a'], kind: 'sunrise' },
] as const;

function flower(c: CanvasRenderingContext2D, x: number, y: number, r: number, petals: number, col: string, center: string): void {
  for (let i = 0; i < petals; i++) {
    const a = (i / petals) * Math.PI * 2;
    c.fillStyle = col;
    c.beginPath();
    c.ellipse(x + Math.cos(a) * r * 0.6, y + Math.sin(a) * r * 0.6, r * 0.55, r * 0.28, a, 0, Math.PI * 2);
    c.fill();
  }
  c.fillStyle = center;
  c.beginPath();
  c.arc(x, y, r * 0.3, 0, Math.PI * 2);
  c.fill();
}

export function elderCard(theme: number, big: string, sub: string): string {
  const key = `${theme}|${big}|${sub}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const W = 240, H = 300;
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const c = cv.getContext('2d')!;
  const th = THEMES[theme % THEMES.length];
  const g = c.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, th.sky[0]);
  g.addColorStop(1, th.sky[1]);
  c.fillStyle = g;
  c.fillRect(0, 0, W, H);

  // Sun / moon with rays.
  const night = th.kind === 'moon';
  c.save();
  c.translate(W * 0.78, H * 0.18);
  if (!night) {
    for (let i = 0; i < 16; i++) {
      c.rotate(Math.PI / 8);
      c.fillStyle = 'rgba(255,240,170,0.55)';
      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(80, -6);
      c.lineTo(80, 6);
      c.fill();
    }
  }
  c.fillStyle = night ? '#fff6c8' : '#ffdf5a';
  c.beginPath();
  c.arc(0, 0, night ? 22 : 26, 0, Math.PI * 2);
  c.fill();
  c.restore();
  if (night) {
    for (let i = 0; i < 40; i++) {
      c.fillStyle = 'rgba(255,255,255,0.9)';
      c.fillRect(hash2(i, 1, 7) * W, hash2(i, 2, 7) * H * 0.6, 2, 2);
    }
  }

  // Scenery.
  const bottom = H * 0.72;
  if (th.kind === 'mountains' || th.kind === 'sunrise' || th.kind === 'bamboo') {
    c.fillStyle = th.kind === 'sunrise' ? '#c45a7a' : '#6fbf8f';
    c.beginPath();
    c.moveTo(0, bottom);
    c.lineTo(60, bottom - 90);
    c.lineTo(120, bottom - 30);
    c.lineTo(180, bottom - 110);
    c.lineTo(W, bottom - 40);
    c.lineTo(W, H);
    c.lineTo(0, H);
    c.fill();
    c.fillStyle = 'rgba(255,255,255,0.6)';
    c.beginPath();
    c.moveTo(180, bottom - 110);
    c.lineTo(165, bottom - 85);
    c.lineTo(195, bottom - 85);
    c.fill();
  }
  if (th.kind === 'bamboo') {
    for (let i = 0; i < 6; i++) {
      const x = 20 + i * 40;
      c.fillStyle = '#4fa35a';
      c.fillRect(x, 80, 8, H);
      for (let j = 100; j < H; j += 40) c.fillRect(x - 2, j, 12, 3);
    }
  }
  // Ground of flowers.
  c.fillStyle = 'rgba(80,160,90,0.85)';
  c.fillRect(0, bottom + 20, W, H);
  const flowerCol = th.kind === 'roses' ? '#ff4f7a' : th.kind === 'sunflower' ? '#ffcf2e' : th.kind === 'lotus' ? '#ff9fc6' : '#ffffff';
  for (let i = 0; i < 9; i++) {
    const x = 15 + i * 27 + hash2(i, 3, theme) * 10, y = bottom + 40 + hash2(i, 4, theme) * 30;
    flower(c, x, y, th.kind === 'sunflower' ? 20 : 15, th.kind === 'sunflower' ? 12 : 6, flowerCol, th.kind === 'sunflower' ? '#7a4a20' : '#ffe066');
  }
  if (th.kind === 'tea') {
    c.fillStyle = '#ffffff';
    c.beginPath();
    c.ellipse(W / 2, bottom + 10, 42, 12, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = '#e3f2ff';
    c.fillRect(W / 2 - 34, bottom - 30, 68, 40);
    c.fillStyle = '#a86a3a';
    c.beginPath();
    c.ellipse(W / 2, bottom - 30, 34, 8, 0, 0, Math.PI * 2);
    c.fill();
    c.strokeStyle = 'rgba(255,255,255,0.8)';
    c.lineWidth = 3;
    for (const dx of [-12, 0, 12]) {
      c.beginPath();
      c.moveTo(W / 2 + dx, bottom - 40);
      c.bezierCurveTo(W / 2 + dx - 10, bottom - 60, W / 2 + dx + 10, bottom - 70, W / 2 + dx, bottom - 90);
      c.stroke();
    }
  }

  // Sparkles.
  for (let i = 0; i < 24; i++) {
    const x = hash2(i, 5, theme) * W, y = hash2(i, 6, theme) * H;
    c.fillStyle = 'rgba(255,255,255,0.85)';
    c.save();
    c.translate(x, y);
    c.rotate(Math.PI / 4);
    c.fillRect(-1, -5, 2, 10);
    c.fillRect(-5, -1, 10, 2);
    c.restore();
  }

  // Big outlined greeting text, the hallmark of the style.
  const fit = (text: string, size: number, maxW: number) => {
    let s = size;
    c.font = `900 ${s}px "PingFang TC","Noto Sans TC","Microsoft JhengHei",system-ui,sans-serif`;
    while (c.measureText(text).width > maxW && s > 14) {
      s -= 2;
      c.font = `900 ${s}px "PingFang TC","Noto Sans TC","Microsoft JhengHei",system-ui,sans-serif`;
    }
    return s;
  };
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  fit(big, 44, W - 24);
  c.lineJoin = 'round';
  c.lineWidth = 9;
  c.strokeStyle = '#ffffff';
  c.strokeText(big, W / 2, H * 0.42);
  c.lineWidth = 4;
  c.strokeStyle = '#c0185a';
  c.strokeText(big, W / 2, H * 0.42);
  const tg = c.createLinearGradient(0, H * 0.36, 0, H * 0.48);
  tg.addColorStop(0, '#ffe14d');
  tg.addColorStop(1, '#ff7a1a');
  c.fillStyle = tg;
  c.fillText(big, W / 2, H * 0.42);
  fit(sub, 18, W - 30);
  c.lineWidth = 5;
  c.strokeStyle = '#ffffff';
  c.strokeText(sub, W / 2, H * 0.55);
  c.fillStyle = '#d6246b';
  c.fillText(sub, W / 2, H * 0.55);

  const url = cv.toDataURL('image/png');
  cache.set(key, url);
  return url;
}
